import * as ce from "./custom_errors";
import { documentFile } from "./rest_utils";
import { parse, ARR, OBJ } from "partial-json";
import { SavedSelection } from "./session_storage_utils";

export const documentNamePrefix: string = "(JWAI)_";

const officeReadyPromise = new Promise<void>((resolve, reject) => {
  Office.onReady((info) => {
    if (info.host === Office.HostType.Word) {
      resolve();
    } else {
      reject(new Error("Host is not Word"));
    }
  });
});

async function init() {
  await officeReadyPromise;
}
init();

export function getDocumentName(): string {
  const documentUrl = Office.context.document.url;
  let documentName = "";

  if (documentUrl === null || documentUrl === undefined || documentUrl === "") {
    documentName = "Unnamed Document";
  } else {
    documentName = documentUrl.split("/").pop()!.split("\\").pop()!;
  }

  documentName = documentNamePrefix + documentName;
  return documentName;
}

/**
 * This function gets the current document text content.
 *
 * @returns {Promise<string>} The current document text content.
 */
async function getDocContent(): Promise<string> {
  return Word.run(async (context) => {
    const body = context.document.body;
    body.load("text");
    await context.sync();
    return body.text;
  });
}

/**
 * This function gets the current document comments.
 *
 * @returns {Promise<Word.Comment[]>} The current document comments.
 */
async function getDocComments(): Promise<Word.Comment[]> {
  return Word.run(async (context) => {
    if (!Office.context.requirements.isSetSupported("WordApi", "1.4")) {
      console.warn("Word API 1.4 is not supported. Comments may not be available.");
      return [];
    }

    const comments = context.document.body.getComments();
    comments.load("items");
    await context.sync();
    return comments.items;
  });
}

/**
 * This function collects all replies to the provided comments and flattens them into a single array.
 *
 * @param {Word.Comment[]} comments The comments to flatten.
 * @returns {Promise<(Word.Comment | Word.CommentReply)[]>} The flattened comments.
 */
async function flattenComments(
  comments: Word.Comment[]
): Promise<(Word.Comment | Word.CommentReply)[]> {
  return Word.run(async (context) => {
    let flatComments: (Word.Comment | Word.CommentReply)[] = [];

    for (const comment of comments) {
      flatComments.push(comment);

      const replies = comment.replies;

      const commentContext = comment.context;
      commentContext.load(replies, "items");
      await commentContext.sync();

      flatComments = flatComments.concat(replies.items);
    }

    await context.sync();
    return flatComments;
  });
}

/**
 * This function gets the current document text content with comments inserted at the correct positions.
 *
 * @returns {Promise<string>} The current document text content with inserted comments.
 *
 * @throws {DocumentContentError} If an error occurs while getting the document content.
 */
export async function getDocContentWithComments(maxLength: number | null = null): Promise<string> {
  return Word.run(async (context) => {
    let content = await getDocContent();
    const comments = await getDocComments();

    const commentInsertSymbol = "";

    const flattenedComments = await flattenComments(comments);

    let commentCounter = 1;
    // insert comment at symbol position in content
    const combinedContent = content
      .split(commentInsertSymbol)
      .map((part) => {
        const comment = flattenedComments.shift();
        if (comment) {
          if (comment instanceof Word.CommentReply) {
            commentCounter++;
          } else {
            commentCounter = 1;
          }
          return `${part}${commentCounter === 1 ? " " : ""}(Kommentar ${commentCounter} - ${comment.authorName}: ${
            comment.content
          }) `;
        } else {
          return part;
        }
      })
      .join("");

    await context.sync();

    if (combinedContent.length === 0) {
      throw new ce.DocumentContentError("Dokument darf nicht leer sein.");
    } else if (maxLength !== null && combinedContent.length > maxLength) {
      throw new ce.DocumentContentError(
        `Dokument darf nicht länger als ${maxLength} Zeichen sein.`
      );
    }

    return combinedContent;
  }).catch(function (error) {
    console.error(error);
    throw new ce.DocumentContentError("Error getting document content with comments.");
  });
}

export function insertCommentIntoWord(text: string, rangeAreaOffset: number = 0) {
  if (!Office.context.requirements.isSetSupported("WordApi", "1.4")) {
    console.warn("Word API 1.4 is not supported. Comments may not be available.");
    return;
  }

  Word.run(async function (context) {
    const range = context.document.getSelection();
    context.document.body.getRange();
    range.load("comments");
    await context.sync();
    const comments = range.getComments();
    comments.load("items");
    await context.sync();

    if (comments.items.length > 0) {
      const selectedComment = comments.items[0];
      selectedComment.reply(text);
    } else {
      const comment = range.insertComment(text);
    }
    return await context.sync();
  }).catch(function (error) {
    console.log("Fehler: " + error);
  });
}

export async function insertAtCurrentPosition(text: string) {
  return Word.run(async function (context) {
    const range = context.document.getSelection();
    range.load("text");
    await context.sync();
    if (range.text.length > 0) {
      range.insertText(text, "Replace");
    } else {
      range.insertText(text, "After");
    }
    await context.sync();
    return true;
  }).catch(function (error) {
    console.error(error);
    return false;
  });
}

function cleanText(text: string): string {
  const cleanedText = text
    .replace(/[\u0000\u0001\u0003-\u001F\u007F-\u009F]/g, "\n") // non printable and control chars (ASCII < 32 and 127, and 159)
    .replace(/\u0002/g, "") // footnote numbers
    .replace(/\u25A1/g, "*") // "□"
    .normalize("NFC") // unicode normalization
    .replace(/\r\n|\r/g, "\n") // Normalize line endings
    .replace(/\n+/g, "\n") // Normalize line endings
    .replace(/[ \t]+/g, " ") // Normalize whitespace
    .trim();
  return cleanedText;
}

function textToFile(text: string, filename: string): File {
  // Clean the text
  const cleanedText = cleanText(text);
  // Encode and create the Blob
  const blob = new Blob([new TextEncoder().encode(cleanedText)], { type: "text/plain" });
  return new File([blob], filename, { type: "text/plain" });
}

export async function textToContentFile(text: string, contentType: string): Promise<documentFile> {
  const fileName = getDocumentName() + (contentType !== "" ? "." + contentType : "") + ".txt";
  const textFile = textToFile(text, fileName);
  const documentFile: documentFile = {
    name: fileName,
    originFileObj: textFile,
  };
  return documentFile;
}

export async function getContentFile(): Promise<documentFile> {
  const text = await getDocContentWithComments();
  return textToContentFile(text, "docx");
}

// maybe use something like (import stringSimilarity from "string-similarity";) later
function jaccardSimilarity(a: string, b: string): number {
  const setA = new Set(a.toLowerCase());
  const setB = new Set(b.toLowerCase());
  const intersection = new Set([...setA].filter((c) => setB.has(c)));
  const union = new Set([...setA, ...setB]);
  return intersection.size / union.size;
}

const MIN_SIMILARITY = 0.6;
const MIN_LENGTH = 3;
export async function searchTextinDocument(
  searchText: string,
  partial: boolean = false,
  markIndex?: number
): Promise<number> {
  return Word.run(async (context) => {
    try {
      const searchPartials: string[] = partial ? searchText.split(/[\s\/\-_]+/) : [searchText];
      for (let i = 0; i < searchPartials.length; i++) {
        searchPartials[i] = searchPartials[i].trim().toLocaleLowerCase();
      }

      const body = context.document.body;

      let searchResults: Word.Range[] = [];
      for (const searchPartial of searchPartials) {
        const searchResult = body.search(searchPartial, {
          matchCase: false,
          matchWholeWord: true,
        });
        searchResult.load("items");
        await context.sync();

        if (searchResult.items.length > 0) {
          searchResults = searchResults.concat(searchResult.items);
        }
      }

      const reverseSearchFallback = true;
      if (searchResults.length === 0 && reverseSearchFallback) {
        const paragraphs = body.paragraphs;
        paragraphs.load("items");
        await context.sync();

        for (const paragraph of paragraphs.items) {
          const wordRanges = paragraph.getTextRanges([" ", "\n"], true);
          wordRanges.load("items");
          await context.sync();

          if (wordRanges.items.length === 0) continue;

          for (const wordRange of wordRanges.items) {
            const text = wordRange.text.toLocaleLowerCase().trim();
            const cleanText = text.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "");
            if (cleanText.length < MIN_LENGTH) continue;
            for (const searchPartial of searchPartials) {
              const similarity = jaccardSimilarity(cleanText, searchPartial);
              if (similarity >= MIN_SIMILARITY && searchPartial.includes(cleanText)) {
                searchResults.push(wordRange);
                break;
              }
            }
          }
        }
      }

      if (markIndex !== undefined && searchResults.length > 0) {
        if (markIndex >= 0 && markIndex <= searchResults.length) {
          searchResults[markIndex].select();
          await context.sync();
        }
      }

      return searchResults.length;
    } catch (error) {
      console.warn("Error searching text in document: ", error);
      return 0;
    }
  });
}

async function getOriginalDocumentHash(taskName: string): Promise<string | null> {
  return Word.run(async (context) => {
    const propName = `documentHash_${taskName}`;
    const originalHash = context.document.properties.customProperties.getItemOrNullObject(propName);
    await context.sync();
    if (originalHash.isNullObject) {
      return null;
    }
    originalHash.load("value");
    await context.sync();

    return originalHash.value ?? null;
  });
}

export async function setOriginalDocumentHash(taskName: string): Promise<void> {
  return Word.run(async (context) => {
    const propName = `documentHash_${taskName}`;
    const currentHash = await getDocumentHash();
    const originalHash = context.document.properties.customProperties.getItemOrNullObject(propName);
    await context.sync();
    if (originalHash.isNullObject) {
      context.document.properties.customProperties.add(propName, currentHash);
      await context.sync();
      return;
    }
    originalHash.load("value");
    await context.sync();
    originalHash.value = currentHash;
    await context.sync();
  });
}

async function getDocumentHash(text?: string): Promise<string> {
  text ??= await getDocContentWithComments();
  const hashBuffer = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function isDocumentChanged(taskName: string): Promise<boolean> {
  const originalHash = await getOriginalDocumentHash(taskName);
  const currentHash = await getDocumentHash();
  return currentHash !== originalHash;
}

export function onContentChange(callback: () => void) {
  Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, () => {
    callback();
  });
}

// TODO: currently not used - remove?
export function onSelectionChange(callback: (selectedText: string) => void) {
  Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, async () => {
    try {
      const selectedText = await getSelectedText();
      if (selectedText !== null) {
        callback(selectedText);
      }
    } catch (error) {
      console.error("Error getting selected text:", error);
    }
  });
}

export async function getSelectedText(): Promise<string> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.load("text");
    await context.sync();
    return selection.text;
  });
}

export async function getSelectedTextHtml(withOuterDiv: boolean = true): Promise<string> {
  return Word.run(async (context) => {
    try {
      const selection = context.document.getSelection();
      await context.sync();
      const selectedHtml = selection.getHtml();
      await context.sync();
      const parser = new DOMParser();
      let parsedHtml = parser.parseFromString(selectedHtml.value, "text/html");

      const nodes = parsedHtml.body.querySelectorAll("p.MsoNormal");
      for (let i = nodes.length - 1; i >= 0; i--) {
        if (nodes[i].innerHTML.trim() === "&nbsp;") {
          nodes[i].remove();
          break;
        }
      }

      // selection workaround (mostly for enumarations)
      selection.paragraphs.load("items");
      await context.sync();
      const paragraphs = selection.paragraphs.items;
      const containsListItem = paragraphs.some((p) => p.isListItem);
      if (containsListItem) {
        const insideWordSelection = parsedHtml.body.firstElementChild?.innerHTML ?? "";
        if (insideWordSelection.trim() !== "") {
          const documentHtmlBody = await getHtmlBodyContent();
          const documentMatchingSelection = getSelectionHtmlIgnoringNumbers(
            documentHtmlBody,
            insideWordSelection
          );
          // parsedHtml = parser.parseFromString(documentMatchingSelection, "text/html");
          parsedHtml.body.firstElementChild!.innerHTML = documentMatchingSelection;
        }
      }

      // let innerHtml = parsedHtml.body.innerHTML;
      let innerHtml =
        parsedHtml.body.firstElementChild && !withOuterDiv
          ? parsedHtml.body.firstElementChild.innerHTML
          : parsedHtml.body.innerHTML;

      innerHtml = innerHtml.replace(
        /<div class="WordSection1">&gt;/g,
        '<div class="WordSection1">'
      );

      return innerHtml;
    } catch (error) {
      // nothing selected
      return "";
    }
  });
}

function getSelectionHtmlIgnoringNumbers(documentHtml: string, selectionHtml: string): string {
  const normalizedSelection = normalizeListNumbers(selectionHtml.trim());
  const normalizedDoc = normalizeListNumbers(documentHtml.trim());

  // console.log("Normalized Selection:", normalizedSelection);
  // console.log("Normalized Document:", normalizedDoc);

  const startIndex = normalizedDoc.indexOf(
    normalizedSelection.slice(0, normalizedSelection.length / 2)
  );
  if (startIndex === -1) {
    console.warn("Selection not found in document (after normalizing numbers)");
    return selectionHtml;
  }

  const endIndex = startIndex + normalizedSelection.length;
  const res = documentHtml.slice(startIndex, endIndex);

  return res;
}

function normalizeListNumbers(html: string): string {
  return html.replace(/\b\d+\./g, "X.");
}

async function searchParagraphs(
  context: Word.RequestContext,
  searchText: string
): Promise<Word.Range | null> {
  try {
    const body = context.document.body;
    const paragraphs = body.paragraphs;
    paragraphs.load("items");
    await context.sync();

    for (const paragraph of paragraphs.items) {
      const paragraphText = paragraph.text.toLowerCase();
      if (
        paragraphText.includes(searchText.toLowerCase()) ||
        searchText.toLowerCase().includes(paragraphText)
      ) {
        return paragraph.getRange();
      }
    }
    return null;
  } catch (error) {
    console.error("Error searching paragraphs:", error);
    return null;
  }
}

async function searchTextRange(
  context: Word.RequestContext,
  searchText: string
): Promise<Word.Range | null> {
  try {
    searchText = searchText.trim();
    if (searchText.length === 0) {
      console.warn("Search text is empty. Cannot search for an empty string.");
      return null;
    }

    const body = context.document.body;

    const maxLength = 250;
    // const half = Math.floor((maxLength - 1) / 2);
    const searchTextStart = searchText.slice(0, maxLength);
    const searchTextEnd = searchText.slice(-maxLength);

    // console.log("Searching Start:", searchTextStart);
    const searchStartCollection = body.search(searchTextStart, {
      matchCase: false,
      matchWholeWord: false,
    });
    searchStartCollection.load("items");
    await context.sync();

    let searchStart: Word.Range | null = null;
    if (searchStartCollection.items.length === 0) {
      // console.warn("Search start not found. Falling back to paragraphs search.");
      // Fallback to searching paragraphs
      const searchTextStartSplit = searchTextStart.split(/[\s\/\-_]+/);
      const firstStartSplit = searchTextStartSplit[0];
      searchStart = await searchParagraphs(context, firstStartSplit);
      if (searchStart === null) {
        console.warn("Search start not found in paragraphs either");
        return null;
      }
    } else {
      searchStart = searchStartCollection.items[0];
    }

    // console.log("Searching End:", searchTextEnd);
    const searchEndCollection = body.search(searchTextEnd, {
      matchCase: false,
      matchWholeWord: false,
    });
    searchEndCollection.load("items");
    await context.sync();

    let searchEnd: Word.Range | null = null;
    if (searchEndCollection.items.length === 0) {
      // console.warn("Search end not found. Falling back to paragraphs search.");
      // Fallback to searching paragraphs
      const searchTextEndSplit = searchTextEnd.split(/[\s\/\-_]+/);
      const lastEndSplit = searchTextEndSplit[searchTextEndSplit.length - 1];
      searchEnd = await searchParagraphs(context, lastEndSplit);
      if (searchEnd === null) {
        console.warn("Search end not found in paragraphs either");
        return null;
      }
    } else {
      searchEnd = searchEndCollection.items[0];
    }

    const documentTextSelection = searchStart.expandTo(searchEnd);

    const textLengthLowerBound = searchText.length * 0.9;
    const textLengthUpperBound = searchText.length * 1.1;
    documentTextSelection.load("text");
    await context.sync();
    if (
      documentTextSelection.text === "" ||
      documentTextSelection.text.length < textLengthLowerBound ||
      documentTextSelection.text.length > textLengthUpperBound
    ) {
      console.warn("No suitable document text found for the search text.");
      return null;
    }

    return documentTextSelection;
  } catch (error) {
    console.error("Error searching text range:", error);
    return null;
  }
}

export async function replaceSavedSelection(
  savedSelection: SavedSelection,
  replacementHTML: string
): Promise<void> {
  return Word.run(async (context) => {
    if (savedSelection.text === null) {
      console.warn("No saved selection text to search with. Cannot replace selection.");
      return;
    }

    const documentTextSelection = await searchTextRange(context, savedSelection.text);
    if (documentTextSelection === null) {
      console.warn("No text found for saved selection. Cannot replace selection.");
      return;
    }

    // documentTextSelection.insertHtml(replacementHTML, "Replace");
    // await context.sync();

    // also use insertHtmlAtSelection function
    documentTextSelection.select();
    await context.sync();
    await insertHtmlAtSelection(replacementHTML, "Replace");
  });
}

export async function searchAndSelectText(searchText: string): Promise<void> {
  return Word.run(async (context) => {
    const documentTextSelection = await searchTextRange(context, searchText);
    if (documentTextSelection === null) {
      console.warn("No selection found for searched text. Cannot select selection.");
      return;
    }

    documentTextSelection.select();
    await context.sync();
  });
}

export async function searchAndDeleteText(searchText: string): Promise<boolean> {
  return Word.run(async (context) => {
    const documentTextSelection = await searchTextRange(context, searchText);
    if (documentTextSelection === null) {
      console.warn("No selection found for searched text. Cannot delete selection.");
      return false;
    }

    documentTextSelection.select();
    await context.sync();
    documentTextSelection.delete();
    await context.sync();
    return true;
  });
}

export async function searchAndReplaceText(
  searchText: string,
  replacementText: string
): Promise<boolean> {
  return Word.run(async (context) => {
    const documentTextSelection = await searchTextRange(context, searchText);
    if (documentTextSelection === null) {
      console.warn("No selection found for searched text. Cannot replace selection.");
      return false;
    }

    documentTextSelection.insertText(replacementText, "Replace");
    await context.sync();
    documentTextSelection.select();
    await context.sync();
    return true;
  });
}

export function decodeHtmlEntities(input: string): string {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = input;
  return textarea.value;
}

// DID NOT WORK OUT...
export async function insertHtmlAtSelectionMarker(
  html: string,
  where: Word.InsertLocation | "Replace" | "Start" | "End" | "Before" | "After" // really no type?...
): Promise<void> {
  return Word.run(async (context) => {
    const uniqueId = "marker-" + Date.now();
    const openingMarkerTag = `<div class="${uniqueId}">`; // class, because id is not allowed in Word
    const closingMarkerTag = "</div>";

    const selection = context.document.getSelection();
    let selectionContent = await getSelectedTextHtml(false);

    if (!selectionContent.trim()) {
      selectionContent = "&nbsp;";
    }
    console.log("selectionContent: ", selectionContent);

    const markedSelectionHtml = openingMarkerTag + selectionContent + closingMarkerTag;
    console.log("markedSelectionHtml: ", markedSelectionHtml);
    try {
      selection.insertHtml(markedSelectionHtml, "Replace");
      await context.sync();

      const documentHtml = await getHtmlContent();
      console.log("Full HTML after marker insert:", documentHtml);
      const parser = new DOMParser();
      const parsedHtml = parser.parseFromString(documentHtml, "text/html");

      const markerElement = parsedHtml.getElementsByClassName(uniqueId);
      if (markerElement.length === 0) {
        console.error("Marker element not found in the document.");
        return;
      }
      markerElement[0].innerHTML = html;

      const replacementHtml = parsedHtml.documentElement.outerHTML;
      context.document.body.insertHtml(replacementHtml, "Replace");
      await context.sync();
    } catch (error) {
      console.error("Error inserting HTML at selection:", error);
    } finally {
      try {
        const documentHtml = await getHtmlContent();
        const parser = new DOMParser();
        const parsedHtml = parser.parseFromString(documentHtml, "text/html");
        const markerElement = parsedHtml.getElementsByClassName(uniqueId);
        if (markerElement.length === 0) return;
        if (markerElement[0].innerHTML.trim() === "&nbsp;") {
          markerElement[0].remove(); // remove the marker if it is empty
        } else {
          markerElement[0].outerHTML = markerElement[0].innerHTML; // remove the marker but keep the content
        }
        context.document.body.insertHtml(parsedHtml.documentElement.outerHTML, "Replace");
        await context.sync();
      } catch (error) {
        console.error("Error removing marker after HTML insertion:", error);
      }
    }
  });
}

export async function insertHtmlAtSelection(
  html: string,
  where: Word.InsertLocation | "Replace" | "Start" | "End" | "Before" | "After" // really no type?...
): Promise<void> {
  return Word.run(async (context) => {
    let cleanedHtml = html;
    // cleanedHtml = decodeHtmlEntities(html);
    // cleanedHtml = cleanedHtml.replace(/&quot;/g, "'");
    // console.log("cleanedHtml: ", cleanedHtml);

    const parser = new DOMParser();
    // const parsedHtml = parser.parseFromString(cleanedHtml, "text/html");

    // const xmlInsertPart = parser.parseFromString(cleanedHtml, "application/xml");
    // console.log("xmlInsertPart: ", xmlInsertPart);

    // parsedHtml.body.querySelectorAll("div.WordSection1").forEach((el) => {
    //   el.attributes.removeNamedItem("class");
    // });
    // cleanedHtml = parsedHtml.documentElement.outerHTML;
    const documentHtml = parser.parseFromString(await getHtmlContent(), "text/html");
    documentHtml.body.innerHTML = cleanedHtml; // "<p>&nbsp;</p>" +
    // documentHtml.querySelectorAll("p").forEach((el) => {
    //   el.style.marginTop = "auto";
    //   el.style.marginBottom = "auto";
    // });

    // Remove first WordSection1 div if it exists
    // const wordSection1 = documentHtml.body.querySelectorAll("div.WordSection1")[0];
    // if (wordSection1) {
    //   wordSection1.outerHTML = wordSection1.innerHTML;
    // }

    cleanedHtml = documentHtml.documentElement.outerHTML;
    // console.log("cleanedHtml: ", cleanedHtml);

    // let a: Element = parsedHtml.body;
    // while (a.childElementCount === 1 && a.firstElementChild) {
    //   a = a.firstElementChild;
    // }
    // const htmlElementsText = a.innerHTML.trim();
    // console.log("htmlElementsText: ", htmlElementsText);
    // cleanedHtml = htmlElementsText;

    cleanedHtml = fixWordImgSrc(cleanedHtml);

    const selection = context.document.getSelection();
    selection.load("text");
    await context.sync();

    // selection.insertOoxml(xmlInsertPart.documentElement.outerHTML, "Replace");
    // await context.sync();
    // const documentHtmlx = await getHtmlContent();
    // console.log("RESULT XML: ", documentHtmlx);
    // return;

    // const leftSelection = selection.expandTo(context.document.body.getRange("Start"));
    // leftSelection.load("text");
    // await context.sync();
    // console.log(
    //   "left text: ",
    //   leftSelection.text.slice(leftSelection.text.length - 20, leftSelection.text.length)
    // );
    // if (!leftSelection.text.endsWith("\n")) {
    //   selection.insertText("\n", "Before");
    //   await context.sync();
    // }

    if (!selection || selection.text === "") {
      selection.insertHtml(cleanedHtml, where);
      await context.sync();
      return;
    }

    const selectionHtml = selection.getHtml();
    await context.sync();

    const currnetSelectionHtml = parser.parseFromString(selectionHtml.value, "text/html");
    const currentSelectionHtmlBody = currnetSelectionHtml.body;

    let resultHtml: string = cleanedHtml;
    if (where === "Before") {
      currentSelectionHtmlBody.getElementsByTagName("p")[0].before(cleanedHtml);
      resultHtml = currentSelectionHtmlBody.innerHTML;
    }
    if (where === "After") {
      currentSelectionHtmlBody.getElementsByTagName("p")[0].after(cleanedHtml);
      resultHtml = currentSelectionHtmlBody.innerHTML;
    }
    selection.insertHtml(resultHtml, "Replace");
    await context.sync();
    // const documentHtml = await getHtmlContent();
    // console.log("RESULT 2: ", documentHtml);
  });
}

export function fixWordImgSrc(html: string): string {
  // add ~ to img src, if missing
  return html.replace(/(src\s*=\s*["'])(?!~)(WRS)/gi, "$1~$2");
}

function normalizeImageKey(src: string): string {
  const lastDash = src.lastIndexOf("-");
  return lastDash !== -1 ? src.substring(lastDash) : src;
}

let srcToBase64Map: Map<string, string> | null = null;
export function resetWordImageMap(): void {
  srcToBase64Map = null;
}
export async function buildWordImageMap(): Promise<Map<string, string>> {
  return Word.run(async (context) => {
    try {
      if (srcToBase64Map !== null) {
        // console.log("Using cached image map.");
        return srcToBase64Map;
      }
      // console.log("Building Word image map...");

      const htmlResult = context.document.body.getHtml();
      await context.sync();
      const html = htmlResult.value;

      const imgSrcList = Array.from(html.matchAll(/<img[^>]+src\s*=\s*["']([^"']+)["']/gi)).map(
        (match) => match[1]
      );

      const images = context.document.body.inlinePictures;
      images.load("items");
      await context.sync();

      const base64List: string[] = [];
      for (const img of images.items) {
        const base64 = img.getBase64ImageSrc();
        await context.sync();
        base64List.push(base64.value);
      }

      const srcToBase64 = new Map<string, string>();
      imgSrcList.forEach((src, i) => {
        if (base64List[i]) {
          const key = normalizeImageKey(src);
          srcToBase64.set(key, `data:image/png;base64,${base64List[i]}`);
        }
      });

      srcToBase64Map = srcToBase64;

      return srcToBase64;
    } catch (error) {
      console.error("Error building Word image map:", error);
      return new Map<string, string>();
    }
  });
}

export async function replaceImagesWithBase64(html: string): Promise<string> {
  const srcToBase64 = await buildWordImageMap();
  // console.log("Image src to base64 map:", srcToBase64);
  return html.replace(/<img\b[^>]*src\s*=\s*["']([^"']+)["'][^>]*>/gi, (match, src) => {
    const key = normalizeImageKey(src);
    // console.log("Found image src key:", key);
    const base64 = srcToBase64.get(key);
    if (base64) {
      return match.replace(src, base64);
    } else {
      return `<img class="llm-image-placeholder" alt="[Bildvorschau nicht verfügbar]">`;
    }
  });
}

// ############################################################################
// ########################## HTML / XML CONTENT ##############################
// ############################################################################

export async function getContentXML(): Promise<string> {
  return Word.run(async (context) => {
    const xml = context.document.body.getOoxml();
    await context.sync();
    return xml.value;
  });
}
export async function getContentXMLFile(): Promise<documentFile> {
  const xml = await getContentXML();
  return textToContentFile(xml, "xml");
}
export async function setContentXML(xml: string): Promise<void> {
  return Word.run(async (context) => {
    try {
      let xmlClean: string = xml.replaceAll(/[\r\n]/g, "");
      try {
        xmlClean = parse(xmlClean, ARR | OBJ);
      } catch (e) {
        console.warn("Error parsing XML, using raw string.");
      }
      context.document.body.insertOoxml(xmlClean, "Replace");
      await context.sync();
    } catch (error) {
      console.error("Error setting content XML:", error);
      throw new ce.DocumentContentError("Error setting content XML");
    }
  });
}

// async function addImageNumberingClasses(): Promise<void> {
//   return Word.run(async (context) => {
//     const body = context.document.body;

//     const htmlResult = body.getHtml();
//     await context.sync();

//     const parser = new DOMParser();
//     const doc = parser.parseFromString(htmlResult.value, "text/html");

//     let counter = 1;
//     doc.querySelectorAll("img").forEach((img) => {
//       img.classList.add(`img-${counter}`);
//       counter++;
//     });

//     body.insertHtml(doc.body.innerHTML, Word.InsertLocation.replace);
//     await context.sync();
//   });
// }

// function extractImagesFromHtml(html: string): string {
//   const parser = new DOMParser();
//   const doc = parser.parseFromString(html, "text/html");

//   let counter = 1;
//   doc.querySelectorAll("img").forEach((img) => {
//     const src = img.getAttribute("src");
//     if (!src) return;

//     const id = `img-${counter++}`;
//     sessionStorage.setItem(id, src);
//     img.removeAttribute("src");
//     img.setAttribute("data-img-id", id);
//   });

//   return doc.body.innerHTML;
// }

// function restoreImagesInHtml(html: string): string {
//   const parser = new DOMParser();
//   const doc = parser.parseFromString(html, "text/html");

//   doc.querySelectorAll("img[data-img-id]").forEach((img) => {
//     const id = img.getAttribute("data-img-id");
//     if (!id) return;

//     const src = sessionStorage.getItem(id);
//     if (src) {
//       img.setAttribute("src", src);
//     }
//     img.removeAttribute("data-img-id");
//   });

//   return doc.body.innerHTML;
// }

export async function getHtmlContent(): Promise<string> {
  return Word.run(async (context) => {
    const body = context.document.body;

    // add classes to all image tags

    const html = body.getHtml();
    await context.sync();
    return html.value;
  });
}

export async function getHtmlContentFile(): Promise<documentFile> {
  const html = await getHtmlContent();
  // const parser = new DOMParser();
  // const parsedHtml = parser.parseFromString(html, "text/html");
  // const htmlBodyText = parsedHtml.documentElement.outerHTML;
  return textToContentFile(html, "html");
}

export async function getHtmlBodyContent(): Promise<string> {
  const html = await getHtmlContent();
  const parser = new DOMParser();
  const parsedHtml = parser.parseFromString(html, "text/html");
  return parsedHtml.body.innerHTML;
}

export async function getHtmlBodyContentFile(): Promise<documentFile> {
  const htmlBodyText = await getHtmlBodyContent();
  return textToContentFile(htmlBodyText, "html");
}

export async function replaceHtmlBodyContent(html: string): Promise<void> {
  Word.run(async (context) => {
    // console.log("Replacing HTML body content with:", html);
    const documentHtml = await getHtmlContent();
    // console.log("Current document HTML:", documentHtml);
    const parser = new DOMParser();
    const parsedHtml = parser.parseFromString(documentHtml, "text/html");
    parsedHtml.body.innerHTML = html;
    // parsedHtml.querySelectorAll("p").forEach((el) => {
    //   el.style.marginTop = "auto";
    //   el.style.marginBottom = "auto";
    // });
    let replacementHtml = parsedHtml.documentElement.outerHTML;
    // console.log("Replacement HTML:", replacementHtml);

    replacementHtml = fixWordImgSrc(replacementHtml);

    context.document.body.insertHtml(replacementHtml, "Replace");
    await context.sync();
  });
}
